import { useEffect, useState, MouseEvent, RefObject, useMemo } from "react";
import { generatePath } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { User } from "@scrile/api-provider/dist/api/UserProvider";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import { useTranslation } from "locales";
import getPublicOrigin from "lib/getPublicOrigin";
import useAuthUser from "hooks/useAuthUser";
import useCustomDomain from "hooks/useCustomDomain";
import { Routes } from "../../router/config";

function useController(produceUser: User | UserPublicData) {
  const { t } = useTranslation();
  const [successfulCopy, setSuccessfulCopy] = useState(false);

  const { user } = useAuthUser();
  const { isCustomDomain, user: userFromDomain } = useCustomDomain();

  // Custom-domain shortcut is skipped in native since the native app
  // is never actually served from a custom domain; forcing the full
  // `/:username/live` path guarantees the public URL is meaningful.
  const isNative = Capacitor.isNativePlatform();

  const chatLink = useMemo(() => {
    const useDomainShortcut =
      !isNative && isCustomDomain && produceUser?.id === userFromDomain?.id;
    const path = useDomainShortcut
      ? Routes.live
      : generatePath(Routes.liveByUsername, { username: produceUser.username });
    return `${getPublicOrigin()}${path}`;
  }, [isNative, isCustomDomain, userFromDomain, produceUser]);

  const mailSubject = t("Live chat invite from {{screenName}}", { screenName: user?.screenName || "" });
  const mailText = t("{{screenName}} has invited you to join a live chat. {{chatLink}}", {
    screenName: user?.screenName || "",
    chatLink,
  });

  const onCopyText = async (e: MouseEvent, ref: RefObject<HTMLInputElement>) => {
    e.stopPropagation();
    if (!ref.current) return;

    // Prefer the Async Clipboard API: works reliably inside Capacitor's
    // WKWebView / Chromium WebView, while document.execCommand("copy")
    // is deprecated and increasingly blocked in modern browsers.
    const text = ref.current.value;
    let ok = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        ok = true;
      }
    } catch {
      // fall through to legacy fallback
    }
    if (!ok) {
      ref.current.select();
      ok = document.execCommand("copy");
      window.getSelection()?.removeAllRanges();
    }
    ref.current.blur();
    if (ok) setSuccessfulCopy(true);
  };

  const onFocusInput = (ref: RefObject<HTMLInputElement>) => {
    if (ref.current) ref.current.select();
  };

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    if (successfulCopy) {
      timeout = setTimeout(() => {
        setSuccessfulCopy(false);
      }, 1000);
    }
    return () => timeout && clearTimeout(timeout);
  }, [successfulCopy]);

  return { onFocusInput, onCopyText, successfulCopy, chatLink, mailSubject, mailText };
}

export default useController;
